import React from 'react';

/**
 * Calculates the horizontal position percentage within an element from a mouse/touch event.
 * Handles different event types including touchend.
 */
export const getOffsetPercentage = (
  event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
  element: HTMLDivElement,
  isRTL: boolean,
): number => {
  const rect = element.getBoundingClientRect();
  let clientX: number;

  if ('touches' in event || 'changedTouches' in event) {
    // Handle TouchEvent (including touchend which uses changedTouches)
    const touchEvent = event as React.TouchEvent<HTMLDivElement>;
    const touchPoint = touchEvent.touches?.[0] ?? touchEvent.changedTouches?.[0];
    if (touchPoint) {
      clientX = touchPoint.clientX;
    } else {
      // Should not happen in a valid touch event, but provide a fallback
      clientX = rect.left; // Default to start of element
    }
  } else {
    const mouseEvent = event as React.MouseEvent<HTMLDivElement>;
    clientX = mouseEvent.clientX;
  }

  const offsetX = Math.max(0, Math.min(rect.width, clientX - rect.left));
  // For RTL, invert the percentage
  const percentage = rect.width > 0 ? offsetX / rect.width : 0;
  return isRTL ? 1 - percentage : percentage;
};
/**
 * Snaps a raw star value (0-5) to the nearest half-star or full-star increment.
 */
export const snapToHalfStar = (rawStarValue: number, snapToHalf: boolean): number => {
  if (snapToHalf) {
    const decimalPart = rawStarValue % 1;
    const integerPart = Math.floor(rawStarValue);

    // Special case for first star to allow snapping to 0
    if (integerPart === 0) {
      if (decimalPart <= 0.3) return 0;
      if (decimalPart <= 0.5) return 0.5;
      return 1;
    }

    return decimalPart <= 0.5 ? integerPart + 0.5 : integerPart + 1;
  } else {
    // Snap to full stars only - use Math.ceil to ensure clicking anywhere on a star selects it
    return Math.ceil(rawStarValue);
  }
};
/**
 * Scales a snapped star value (0-5) to the actual rating value based on maxValue.
 */
export const scaleStarValueToRating = (
  snappedStarValue: number,
  maxValue: number,
  totalStars: number,
): number => {
  const valuePerStar = maxValue / totalStars;
  return snappedStarValue * valuePerStar;
};
/**
 * Rounds the final calculated value to the nearest valid step (half-star value).
 */
export const roundToStep = (value: number, maxValue: number, totalStars: number): number => {
  const valuePerStar = maxValue / totalStars;
  const step = valuePerStar / 2;
  // Avoid division by zero if maxValue is 0
  if (step === 0) return 0;
  return Math.round(value / step) * step;
};
/**
 * Calculates the fill percentage (0-100) for a star at a given index.
 */
export const calculateStarFillPercentage = (
  index: number,
  displayValue: number,
  maxValue: number,
  isRtl: boolean,
  totalStars: number,
): number => {
  const valuePerStar = maxValue / totalStars;
  const displayStarValue = Math.min(totalStars, Math.max(0, displayValue / valuePerStar));

  // In RTL mode, we need to calculate from the opposite end
  const effectiveIndex = isRtl ? totalStars - 1 - index : index;
  const filledPart = displayStarValue - effectiveIndex;

  if (filledPart >= 1) {
    return 100;
  } else if (filledPart > 0) {
    return Math.round(filledPart * 100);
  } else {
    return 0;
  }
};
